import { Component, createRef } from 'react'
import styled from '@emotion/styled'
import { observer } from 'mobx-react'
import PropTypes from 'prop-types'
import events, { getViewport } from '../utils'
import TextErrorField from './TextErrorField'
import _ from 'lodash'

const InputWrapper = styled.div`
  margin-top: 8px;
  min-height: 48px;
  padding: 10px 0;
`

const DropdownBox = styled.div`
  margin: 0px 5px;
  margin-left: ${p => p.leftMost && '0px'};
  position: relative;
  /* Use isDisabled so Emotion does not forward disabled to the div. */
  cursor: ${p => (p.isDisabled ? 'default' : 'pointer')};
  width: ${p => p.width || '160px'};
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: ${p => p.fontSize || '1.125rem'};
  line-height: 1.5rem;
`

const ValueBox = styled.div`
  position: relative;
  background-color: transparent;
  transition: border-bottom 0.3s;
  border-bottom: ${p => (p.active ? '1.5px solid #008ab6' : '1.5px solid #b6c0c4')};

  &:hover {
    border-bottom: ${p => !p.active && !p.isDisabled && '1.5px solid #053240'};
  }
  &::after {
    position: absolute;
    top: 5px;
    right: 8px;
    pointer-events: none;
    font-size: 30px;
    content: '\u2039';
    transform: rotate(-90deg);
    transition: color 0.3s;
    color: ${p => (p.active ? '#008ab6' : '')};
  }
`

const Value = styled.div`
  width: 100%;
  padding-right: 24px;
  padding-bottom: 1px;
  text-align: left;
  display: inline-block;
  justify-content: center;
  white-space: ${p => (p.nowrap ? 'nowrap' : 'normal')};
  word-wrap: ${p => (p.nowrap ? 'normal' : 'break-word')};
  color: ${p => (p.selected ? p.theme.dropdownTextColor : '#b6c0c4')};
  .year & {
    padding-right: 32px;
  }
`

const OptionsBox = styled.ul`
  position: absolute;
  z-index: 100;
  width: calc(100% + 14px);
  overflow-y: auto;
  list-style: none;
  border-radius: 2px;
  padding: 0;
  margin: 0;
  box-shadow:
    0 1px 6px rgba(0, 0, 0, 0.12),
    0 1px 4px rgba(0, 0, 0, 0.24);
  top: 43px;
  max-height: ${p => p.size || 0}px;
  bottom: auto;
  background-color: rgba(240, 243, 245, 1);
  min-height: 160px;
  & li {
    padding: 7px 0 7px 18px;
  }
  & li:first-of-type {
    padding-top: 14px;
  }
  & li:last-child {
    padding-bottom: 14px;
  }
`

const Option = styled.li`
  font-size: 16px;
  line-height: 20px;
  position: relative;
  overflow: hidden;
  color: ${p => (p.selected ? '#038ab7' : 'rgba(119, 119, 119, 0.8)')};
  word-wrap: ${p => (p.nowrap ? 'normal' : 'break-word')};
  display: ${p => p.disabled && 'none'};
  width: 100%;
  &:hover {
    background-color: rgba(79, 173, 204, 0.2);
  }
`

class Dropdown extends Component {
  static propTypes = {
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    error: PropTypes.string,
    forceActive: PropTypes.bool,
    fontSize: PropTypes.string,
    leftMost: PropTypes.bool,
    name: PropTypes.string,
    onClick: PropTypes.func,
    onBlur: PropTypes.func,
    options: PropTypes.array,
    selected: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
    showError: PropTypes.bool,
    switchForceActive: PropTypes.func,
    width: PropTypes.string,
    id: PropTypes.string,
    nowrap: PropTypes.bool,
    source: PropTypes.array,
    valueKey: PropTypes.string,
    value: PropTypes.any,
    allowBlank: PropTypes.bool,
  }

  constructor(props) {
    super(props)

    this.state = {
      active: false,
    }
    this.containerRef = createRef()
  }

  componentDidUpdate(prevProps, prevState) {
    const { forceActive } = this.props
    if (forceActive && !prevProps.forceActive && !this.state.active) {
      this.open()
    }
    if (forceActive === false && prevProps.forceActive && this.state.active) {
      this.close()
    }

    if (prevState.active !== this.state.active) {
      if (!prevState.active && this.state.active) {
        events.addEventsToDocument(this.getDocumentEvents())
      } else if (prevState.active && !this.state.active) {
        events.removeEventsFromDocument(this.getDocumentEvents())
      }
    }
  }

  componentWillUnmount() {
    if (this.state.active) {
      events.removeEventsFromDocument(this.getDocumentEvents())
    }
  }

  getDocumentEvents = () => ({
    click: this.handleDocumentClick,
    touched: this.handleDocumentClick,
  })

  getSelectedItem() {
    const { source, valueKey, value, allowBlank } = this.props
    for (const item of source) {
      if (item[valueKey] === value) return item
    }
    return !allowBlank ? source[0] : undefined
  }

  handleSelect = (item, event) => {
    const { name, onChange, disabled, onBlur } = this.props
    if (!disabled && onChange) {
      onChange(name, item)
      if (onBlur) onBlur(event)
      this.close()
    }
  }

  handleClick = event => {
    const { disabled, onClick } = this.props
    if (disabled) return
    this.state.active ? this.close() : this.open()
    events.pauseEvent(event)
    if (onClick) onClick(event)
  }

  handleDocumentClick = event => {
    const current = this.containerRef.current
    if (this.state.active && current && !current.contains(event.target)) {
      this.close()
    }
  }

  close = () => {
    const { onBlur, name, switchForceActive } = this.props
    if (switchForceActive) switchForceActive(false)
    if (this.state.active) {
      this.setState({ active: false }, () => {
        if (onBlur) onBlur(name)
      })
    }
  }

  open = () => {
    if (this.state.active) return
    this.setState({ active: true })
  }

  getOptionsMaxHeight() {
    if (!this.dropdownBox) return 0
    // Clamp at zero because CSS ignores a negative `max-height`, which would
    // allow the menu to extend past the bottom of the viewport.
    return Math.max(0, getViewport().height - this.dropdownBox.getBoundingClientRect().bottom - 40)
  }

  render() {
    const {
      id,
      disabled,
      error,
      fontSize,
      leftMost = true,
      options = [],
      placeholder,
      selected,
      showError,
      width = '160px',
      nowrap,
    } = this.props
    const { active } = this.state
    const size = this.getOptionsMaxHeight()
    const selectedOption = _.find(options, ({ value }) => value === selected)

    return (
      <InputWrapper ref={this.containerRef}>
        <div ref={box => (this.dropdownBox = box)} onClick={this.handleClick}>
          <DropdownBox
            id={id}
            fontSize={fontSize}
            active={active}
            leftMost={leftMost}
            width={width}
            isDisabled={disabled}>
            <ValueBox active={active} isDisabled={disabled}>
              <Value selected={Boolean(selectedOption)} nowrap={nowrap}>
                {selectedOption ? selectedOption.label : placeholder}
              </Value>
            </ValueBox>
            {active && (
              <OptionsBox size={size}>
                {options.map((opt, index) => (
                  <Option
                    key={index}
                    disabled={opt.disabled}
                    selected={selected === opt.value}
                    onClick={e => this.handleSelect(opt.value, e)}
                    value={opt.value}
                    nowrap={nowrap}>
                    {opt.label}
                  </Option>
                ))}
              </OptionsBox>
            )}
          </DropdownBox>
        </div>
        <TextErrorField error={error} showError={showError} />
      </InputWrapper>
    )
  }
}

export default observer(Dropdown)
